<template>
  <preview-bar v-if="isPreview" />
  <page-breadcrumbs v-if="pageData" :breadcrumb-segments="breadcrumbSegments" :post-id="pageData.databaseId" :product-net-prices="adminNetPrices"/>
  <div class="sound-library">
    <div v-if="pageData" class="sound-library__content-right">
      <div class="sound-library__header-title">
        <h1 class="sound-library__headline">
          {{ pageData.name }}
        </h1>
        <div ref="stickyContent">
          <product-price
            v-if="! showSkeletonLoading"
            class="sound-library__header-price"
            different-sizes
            :product-id="selectedProductId"
            :default-currency="selectedProductPrice?.defaultCurrency"
            :delivery-option="selectedDeliveryOption"
            :show-tax-indicator="true" />
        </div>
        <product-like
          class="sound-library__header-like"
          :product-name="pageData.name"
          :product-id="pageData.databaseId" />
        <div class="sound-library__header-content">
          <div v-if="showDeliveryOptions" class="sound-library__variation-selection">
            <h3 class="sound-library__header-title--title-mid">
              Choose delivery
              <product-delivery-selector
                :buy-out-badge="deliveryBadges.buyOut"
                :subscription-badge="deliveryBadges.subscription"
                @selected-delivery-changed="updateDeliveryMethod" />
            </h3>
            <span class="sound-library__header-subtext" v-if="selectedDeliveryOption === 'subscription'">Minimum subscription term: 3 years.</span>
          </div>
          <div v-if="pageData.variations?.nodes" class="sound-library__variation-selection">
            <h3 class="sound-library__header-title--title-mid">
              Choose variation
            </h3>
            <product-variation-selector
              v-if="pageData.variations?.nodes"
              :selected-product-id="selectedProductId"
              :variations="pageData.variations.nodes"
              :delivery-option="selectedDeliveryOption"
              @variation-changed="updateSelectedProduct"
            />
          </div>
          <div class="sound-library__audio-demo" v-if="audioKey">
            <audio-player
              element-prefix="product"
              show-text
              :trackId="audioKey"
              :show-track-length="false"
              :product-details="productDetails"
            />
          </div>
          <div class="sound-library__specs">
            <h3 class="sound-library__header-title--title-mid">Product Specifications</h3>
            <product-specs
              class="sound-library__product-specs"
              v-if="selectedProductSpecs"
              :product-id="pageData.databaseId"
              :product-specs="selectedProductSpecs"
              :product-categories="pageData.productCategories"
              :product-vendor="pageData.productVendor"
              :product-name="pageData.name"
              :show-skeleton-loading="showSkeletonLoading"
            />
          </div>
          <buy-product
            v-if="pageData && ! showSkeletonLoading"
            class="sound-library__header-buy"
            button-style="button-only button-large"
            :is-subscription="selectedDeliveryOption === 'subscription'"
            :product-id="selectedProductId"
            :product-subscription-options="productDetails.productSubscriptionOptions"
            :default-currency="pageData?.defaultCurrency" />
        </div>
      </div>
    </div>
    <div class="sound-library__header-gallery" v-if="highlightImage">
      <div class="sound-library__header-image-wrap">
        <u-i-image-handler
          class="sound-library__header-image"
          :img-src="highlightImage.mediaItemUrl"
          :img-placeholder="highlightImage.mediaItemUrl"
          :img-alt="highlightImage.altText"
          img-sizes="100vw md:605px"
          img-format="avif"
          img-densities="1x 2x" />
        <product-sale-badge
          v-if="selectedProductSaleBadge?.percentageSaleBadge"
          class="sound-library__header-image-sale-badge"
          :percentage-discount="selectedProductSaleBadge.percentageSaleBadge"
          :show-ends-soon="selectedProductSaleBadge.showEndsSoonBadge"
          :show-ends-today="selectedProductSaleBadge.showEndsTodayBadge"
          is-large />
      </div>
      <ul v-if="pageData.galleryImages?.nodes.length > 0" class="sound-library__gallery">
        <li>
          <button class="sound-library__gallery-button" @click="selectedGalleryImage = null">
            <u-i-image-handler
              img-width="150px"
              img-loading="lazy"
              :img-src="pageData.image.mediaItemUrl"
              :img-placeholder="pageData.image.mediaItemUrl"
              :img-alt="pageData.image.altText"
              img-format="avif"
              img-densities="1x 2x" />
          </button>
        </li>
        <li v-for="image in pageData.galleryImages.nodes" :key="image.mediaItemUrl">
          <button class="sound-library__gallery-button" @click="selectedGalleryImage = image">
            <u-i-image-handler
              img-width="150px"
              img-loading="lazy"
              :img-src="image.mediaItemUrl"
              :img-placeholder="image.mediaItemUrl"
              :img-alt="image.altText"
              img-format="avif"
              img-densities="1x 2x" />
          </button>
        </li>
      </ul>
    </div>
    <article v-if="showSkeletonLoading" class="sound-library__skeleton-loading sound-library__skeleton-block"/>
    <article v-if="pageData && ! showSkeletonLoading" class="sound-library__article">
      <div v-if="selectedProductSpecs.variationDetails" class="sound-library__variation-details" v-html="selectedProductSpecs.variationDetails">
      </div>
      <h2 class="sound-library__subheadline sound-library__subheadline--title-mid">
        Product Description
      </h2>
      <vue-enabled-html v-if="pageData.formattedExcerpt" :content="pageData.formattedExcerpt" />
    </article>
  </div>
  <main v-if="showSkeletonLoading" class="sound-library__article sound-library__content--loading">
    <icon-spinner class="sound-library__spinner" />
  </main>
  <main v-if="pageData && ! showSkeletonLoading" class="sound-library__content">
    <vue-enabled-html :content="pageData.formattedContent" />
  </main>

  <div v-if="pageData" class="sound-library__related">
    <related-products
      header-text="Related Products"
      use-slider
      order-by="relevance"
      :product-ids="[pageData.databaseId]" />
  </div>
</template>
  
<script lang="ts">
import { useAudioPlayerStore } from "~/stores/audioplayer";
import { getAudioKey } from "~/utils/audioplayer";
import { usePriceStore } from "~/stores/priceStore";
import { useUserStore } from "~/stores/user";
import { defineAsyncComponent } from '#imports';

const RelatedProducts = defineAsyncComponent(() => import('~/components/blocks/RelatedProducts.vue'));
const IconSpinner = defineAsyncComponent(() => import('~/assets/icons/IconSpinner.svg'));

export default {
  components: {
    RelatedProducts,
    IconSpinner,
  },
  props: {
    pageData: Object,
    showSkeletonLoading: Boolean,
    isPreview: Boolean,
  },
  setup() {
    return {
      audioPlayerStore: useAudioPlayerStore(),
      priceStore: usePriceStore(),
      userStore: useUserStore(),
    }
  },
  mounted() {
    // IntersectionObserver watches the price (stickyContent ref)
    // Triggers footer audio player only when price scrolls out of viewport from TOP
    // Prevents triggering when scrolling back up or when price leaves from bottom which can happen on mobile
    this.observer = new IntersectionObserver((entries: IntersectionObserverEntry[]) => {
      const entry = entries[0];
      const isSticky = !entry.isIntersecting;

      // Track if element has been visible at least once
      if (entry.isIntersecting) {
        this.hasBeenVisible = true;
      }

      if (isSticky) {
        // No demo, nothing to play
        if (!this.audioKey) {
          return;
        }

        // Only trigger after element has been visible once
        if (!this.hasBeenVisible) {
          return;
        }
        
        // Skip auto-trigger on landscape mobile - only show on manual play
        if (this.isLandscapeMobile) {
          return;
        }
        
        // Check if element left viewport from the top (not bottom)
        // If boundingClientRect.top < 0, it's scrolling out from the top
        // If boundingClientRect.top > window.innerHeight, it left from bottom
        const rect = entry.boundingClientRect;
        const leftFromTop = rect.top < 0;
        const leftFromBottom = rect.top > window.innerHeight;
        
        // Only trigger audio player if element scrolled out from the top
        if (!leftFromTop || leftFromBottom) {
          return;
        }
        
        const { loadedProductDetails } = this.audioPlayerStore;
        if (loadedProductDetails?.productId === this.productDetails.productId) {
          this.audioPlayerStore.makeFooterBarVisible();
        } else {
          this.audioPlayerStore.loadFooterAudio(this.productDetails, false, true);
        }
      }
    }, {
      rootMargin: `0px 0px 0px 0px`,
      threshold: [0.99],
    });
    this.observer.observe(this.$refs.stickyContent as HTMLElement);
  },
  beforeUnmount() {
    this.observer?.disconnect();
    // Hide footer bar on navigating away if it was only visible because of scrolling
    const { loadedProductDetails, isPlaying } = this.audioPlayerStore;
    if (loadedProductDetails?.productId === this.productDetails.productId && !isPlaying) {
      this.audioPlayerStore.loadFooterAudio(null);
    }
  },
  data() {
    return {
      selectedProductId: this.pageData?.databaseId,
      productDetails: {
        productId: this.pageData?.databaseId,
        productImage: this.pageData?.image?.mediaItemUrl,
        productName: this.pageData?.name,
        productLiked: false,
        defaultCurrency: this.pageData?.defaultCurrency,
        soundcloudId: this.pageData?.productSpecs?.node?.productSoundcloudCode || this.pageData?.soundcloudId,
        soundcloudIdType: this.pageData?.productSpecs?.node?.soundcloudShortcode?.includes('/playlists/') ? 'playlist' : 'track',
        audioDemos: this.pageData?.productSpecs?.node?.audioDemos ?? this.pageData?.audioDemos ?? [],
        variations: this.pageData?.variations,
        productSubscriptionOptions: this.pageData?.productSubscriptionOptions,
        productVendor: this.pageData?.productVendor,
      },
      selectedDeliveryOption: 'buy-out',
      selectedGalleryImage: null,
      observer: null as IntersectionObserver|null,
      hasBeenVisible: false,
      useClientSideBadges: false,
      clientSideBadge: null,
      adminNetPrices: null as Array<{name: string|null, priceExTax: string}> | null,
    }
  },
  computed: {
    audioKey() {
      return getAudioKey({
        audioDemos: this.pageData?.productSpecs?.node?.audioDemos ?? this.pageData?.audioDemos,
        soundcloudId: this.pageData?.productSpecs?.node?.productSoundcloudCode || this.pageData?.soundcloudId,
      })
    },
    isLandscapeMobile() {
      if (process.client) {
        const isMobile = window.innerWidth <= 768;
        const isLandscape = window.innerWidth > window.innerHeight;
        return isMobile && isLandscape;
      }
      return false;
    },
    breadcrumbSegments() {
      const breadcrumbSegments = [];

      // Product type [Sound Effect, Plug-in]
      if (! this.isPlugin()) {
        breadcrumbSegments.push({
          name: 'Sound Effect',
          url: '/shop/sound-effects',
        })
      } else {
        breadcrumbSegments.push({
          name: 'Plug-in',
          url: '/shop/category/plugins',
        })
      }

      // Product vendor
      if (this.pageData?.productVendor) {
        breadcrumbSegments.push({
          name: this.pageData.productVendor.name,
          url: `/shop/sound-designer/${this.pageData.productVendor.slug}`,
        })
      }

      // Product name
      if (this.productDetails?.productName) {
        breadcrumbSegments.push({
          name: this.productDetails.productName
        })
      }

      return breadcrumbSegments
    },
    selectedProductPrice() {
      // get variation product price
      if (this.pageData?.variations?.nodes?.length && this.selectedProductId !== this.pageData?.databaseId) {
        const variation = this.pageData.variations.nodes.find((variation) => variation.databaseId === this.selectedProductId)

        // if variation and subscription selected, get relevant price
        if (variation && this.selectedDeliveryOption === 'subscription') {
          const subscriptionPrices = variation.productSubscriptionOptions
          return {
            defaultCurrency: {
              price: subscriptionPrices.price,
              regularPrice: subscriptionPrices.regularPrice,
              salePrice: subscriptionPrices.salePrice,
              priceExTax: subscriptionPrices.priceExTax ?? null,
              regularPriceExTax: subscriptionPrices.regularPriceExTax ?? null,
              salePriceExTax: subscriptionPrices.salePriceExTax ?? null
            },
          }
        }

        if (variation && variation.defaultCurrency) {
          return {
            defaultCurrency: variation.defaultCurrency,
          }
        }
      }

      // @todo if Not variation and subscription selected, get relevant price

      // get default product price
      return {
        defaultCurrency: this.pageData?.defaultCurrency,
      }
    },
    isSelectedAVariation() {
      return !! this.pageData?.variations?.nodes?.find((variation) => variation.databaseId === this.selectedProductId)
    },
    showDeliveryOptions() {
      // Check if current product is standard product with an available subscription option
      if (! this.isSelectedAVariation && this.pageData?.productSubscriptionOptions) {
        return !! this.pageData?.productSubscriptionOptions?.duration
      }

      if (! this.isSelectedAVariation) {
        return false
      }

      // Check if selected variation can be purchased as subscription
      const variation = this.pageData.variations.nodes.find((variation) => variation.databaseId === this.selectedProductId)

      if (variation?.productSubscriptionOptions?.duration) {
        return true;
      }

      return false;
    },
    selectedProductImage() {
      // get variation product image
      if (this.pageData?.variations?.nodes?.length && this.selectedProductId !== this.pageData?.databaseId) {
        const variation = this.pageData.variations.nodes.find((variation) => variation.databaseId === this.selectedProductId)

        if (variation?.image?.mediaItemUrl) {
          return {
            mediaItemUrl: variation.image.mediaItemUrl,
            altText: variation.image.altText ?? '',
          }
        }
      }

      // get default product image
      return {
        mediaItemUrl: this.pageData?.image?.mediaItemUrl ?? '',
        altText: this.pageData?.image?.altText ?? '',
      }
    },
    selectedProductSpecs() {
      if (! this.pageData?.productSpecs) {
        return {}
      }

      const { node: baseProduct } = this.pageData.productSpecs

      const productSpecs = {
        soundsIncluded: baseProduct.soundsIncluded,
        filesIncluded: baseProduct.filesIncluded,
        format: baseProduct.format,
        totalSize: baseProduct.totalSize,
        metatagged: baseProduct.metatagged,
        type: baseProduct.type,
        totalDuration: baseProduct.totalDuration,
      }

      // get variation product specs
      if (this.pageData?.variations?.nodes?.length && this.selectedProductId !== this.pageData?.databaseId) {
        const variation = this.pageData.variations.nodes.find((variation) => variation.databaseId === this.selectedProductId)

        if (variation && variation.variationProductSpecs) {
          productSpecs.soundsIncluded = variation.variationProductSpecs.numberOfSounds?.length ? variation.variationProductSpecs.numberOfSounds : productSpecs.soundsIncluded
          productSpecs.filesIncluded = variation.variationProductSpecs.numberOfFiles?.length ? variation.variationProductSpecs.numberOfFiles : productSpecs.filesIncluded

          productSpecs.variationDetails = variation.variationProductSpecs.editionNotes ?? ''

          if (productSpecs.variationDetails) {
            productSpecs.showVariationSpecs = true;
          }
        }
      }

      return productSpecs
    },
    // Sale badges of the selected product for both delivery options: { buyOut, subscription }
    saleBadges() {
      // Use client-side calculated badges if currency has changed
      if (this.useClientSideBadges && this.clientSideBadge !== null) {
        return this.clientSideBadge;
      }

      // Otherwise use server-side badges of the selected variation, or of the product itself
      let product = this.pageData;
      if (this.pageData?.variations?.nodes?.length && this.selectedProductId !== this.pageData?.databaseId) {
        product = this.pageData.variations.nodes.find((variation) => variation.databaseId === this.selectedProductId) ?? this.pageData;
      }

      const subscriptionDiscount = product?.productSubscriptionOptions?.percentageDiscount;

      return {
        buyOut: product?.productSaleBadges ?? null,
        subscription: subscriptionDiscount ? {
          percentageSaleBadge: `${subscriptionDiscount}%`,
          showEndsSoonBadge: null,
          showEndsTodayBadge: null,
        } : null,
      }
    },
    selectedProductSaleBadge() {
      return this.selectedDeliveryOption === 'subscription' ? this.saleBadges.subscription : this.saleBadges.buyOut;
    },
    // Simple products show the discounts in the delivery buttons, variable products in the variation selector
    deliveryBadges() {
      if (this.pageData?.variations?.nodes?.length) {
        return { buyOut: null, subscription: null };
      }

      return {
        buyOut: this.saleBadges.buyOut?.percentageSaleBadge || null,
        subscription: this.saleBadges.subscription?.percentageSaleBadge || null,
      }
    },
    currentCurrency() {
      return this.userStore?.getSelectedCurrency;
    },
    highlightImage () {
      if (this.selectedGalleryImage) {
        return this.selectedGalleryImage;
      }

      return this.pageData?.image
    },
  },
  methods: {
    updateDeliveryMethod(selectedDeliveryOption) {
      this.selectedDeliveryOption = selectedDeliveryOption
    },
    isPlugin() {
      if (this.pageData?.productCategories?.nodes) {
        const plugin = this.pageData.productCategories.nodes.find(productCat => productCat.name === 'Plug-Ins')
        return !! plugin
      }

      // Default to false - Sound Effect
      return false;
    },
    async fetchAdminNetPrices(refresh = false) {
      if (!this.userStore.isAdmin || !this.pageData) return;

      const variations = this.pageData.variations?.nodes;
      const products = variations?.length
        ? variations.map(v => ({ id: v.databaseId, name: v.variationName }))
        : [{ id: this.pageData.databaseId, name: null }];

      this.adminNetPrices = await Promise.all(
        products.map(async ({ id, name }) => ({
          name,
          priceExTax: (await this.priceStore.getPrice(id, refresh))?.currentExTax || 'N/A'
        }))
      );
    },
    updateSelectedProduct(productId) {
      this.selectedProductId = productId
      // Recalculate badge for newly selected variation
      if (this.useClientSideBadges) {
        this.recalculateBadgeForCurrency();
      }
    },
    async recalculateBadgeForCurrency() {
      if (process.server) return;

      try {
        this.useClientSideBadges = true;

        const priceData = await this.priceStore.getPrice(this.selectedProductId, true);

        if (! priceData) {
          this.clientSideBadge = null;
          return;
        }

        // Use backend-calculated percentages directly
        let buyOut = null;
        if (priceData.percentageDiscount) {
          // Get time-based badges from original data
          let timeBasedBadges = { showEndsSoonBadge: false, showEndsTodayBadge: false };
          if (this.pageData?.variations?.nodes?.length && this.selectedProductId !== this.pageData?.databaseId) {
            const variation = this.pageData.variations.nodes.find((v) => v.databaseId === this.selectedProductId);
            if (variation?.productSaleBadges) {
              timeBasedBadges = {
                showEndsSoonBadge: variation.productSaleBadges.showEndsSoonBadge || false,
                showEndsTodayBadge: variation.productSaleBadges.showEndsTodayBadge || false,
              };
            }
          } else if (this.pageData?.productSaleBadges) {
            timeBasedBadges = {
              showEndsSoonBadge: this.pageData.productSaleBadges.showEndsSoonBadge || false,
              showEndsTodayBadge: this.pageData.productSaleBadges.showEndsTodayBadge || false,
            };
          }

          buyOut = {
            percentageSaleBadge: `${priceData.percentageDiscount}%`,
            ...timeBasedBadges,
          };
        }

        const subscriptionDiscount = priceData.productSubscriptionOptions?.percentageDiscount;

        this.clientSideBadge = {
          buyOut,
          subscription: subscriptionDiscount ? {
            percentageSaleBadge: `${subscriptionDiscount}%`,
            showEndsSoonBadge: null,
            showEndsTodayBadge: null,
          } : null,
        };
      } catch (error) {
        console.error('Error recalculating sale badge:', error);
        this.useClientSideBadges = false;
      }
    },
  },
  watch: {
    selectedProductImage(newImageObject) {
      this.selectedGalleryImage = newImageObject
    },
    currentCurrency() {
      this.recalculateBadgeForCurrency();
      this.fetchAdminNetPrices(true);
    },
    'userStore.isAdmin': {
      handler(isAdmin) {
        // Fetch admin net prices when admin status becomes available (after auth loads)
        if (isAdmin && !this.adminNetPrices) {
          this.fetchAdminNetPrices();
        }
      },
      immediate: true,
    },
  }
}
</script>
  
<style lang="scss">
@use "~/common/scss/variables";
@use "~/common/scss/container";
@use "~/common/scss/button";
@use "~/common/scss/mq";
@use "~/common/scss/typography";
@use "~/common/scss/helpers";
@use "~/common/scss/skeleton";

.sound-library {
  @include skeleton.loading();

  padding-top: helpers.px-to-rem(60);
  display: grid;
  gap: helpers.px-to-rem(54);
  grid-template: "image meta" auto "shortDescription meta" auto / 1fr 1fr;
  width: 95%;
  min-height: 95vh;
  max-width: variables.$containerMedium;
  margin: 0 auto;

  @include mq.mq(variables.$mq-1-max) {
    grid-template: "image" auto "meta" auto "shortDescription" auto / 100%;
  }

  &__spinner {
    @include button.spinner();
  }

  &__header-image-wrap {
    position: relative;
    display: block;
    margin: 0 auto;
    max-width: helpers.px-to-rem(605);
    width: 100%;
  }

  &__header-image-sale-badge {
    top: -0.8rem;
    right: -0.7rem;
  }

  &__header-image {
    aspect-ratio: 1 / 1;
  }

  &__content-right {
    grid-area: meta;
    padding: 1rem;
    height: max-content;

    @include mq.mq(variables.$mq-2-min) {
      position: sticky;
      top: var(--menu-height);
    }
  }

  &__header-gallery {
    grid-area: image;

    img {
      aspect-ratio: 1 / 1;
      object-fit: contain;
      width: 100%;
    }
  }

  &__gallery {
    align-items: center;
    display: grid;
    gap: .25rem;
    grid-template-columns: repeat(5, 1fr);
    list-style: none;
  }

  &__header-title {
    display: grid;
    grid-area: headline;
    grid-template: "title heart" auto "price heart" auto "content content" auto / 1fr auto;
    &--title-mid {
      font-size: helpers.px-to-rem(20);
      font-weight: 600;
    }
  }

  &__header-subtext {
    padding-top: 0.3em;
    display: block;
    text-align: right;
  }

  &__header-headline {
    grid-area: title;
  }

  &__headline {
    @include typography.h2();

    font-weight: 800;
    grid-area: title;
  }

  &__header-price {
    align-items: baseline;
    grid-area: price;
  }

  &__header-like {
    --icon-color: #{variables.$colorBlack};
    font-size: 3rem;
    grid-area: heart;
  }

  &__header-content {
    align-items: stretch;
    display: flex;
    flex-flow: column;
    gap: 2.5rem;
    grid-area: content;
    padding-top: helpers.px-to-rem(20);
  }

  &__header-buy {
    width: 100%;
  }

  &__subheadline {
    @include typography.h4();

    margin-bottom: helpers.px-to-rem(18);
  }

  &__variation-details {
    margin: 2rem 0;
  }

  &__article {
    grid-area: shortDescription;
    text-align: left;

    p {
      margin-bottom: 1rem;
    }

    img {
      max-width: 100%;
    }

    // Responsive YouTube embeds in short description
    .content iframe {
      width: 100%;
      height: auto;
      aspect-ratio: 16 / 9;
      max-width: 100%;
      display: block;
    }
  }

  &__article center {
    text-align: left;
  }

  &__content,
  &__related {
    @include container.l();
  }

  &__content {
    display: flex;
    flex-direction: column;
    margin-bottom: 6rem;
    margin-top: 6rem;
    width: 95%;

    &--loading {
      text-align: center;
      margin: 4rem auto;
    }

    .well {
      display: flex;
      flex-direction: column;
      width: calc(100% - 10px);

      div {
        padding: .25rem .35rem;
      }
    }

    p {
      margin-bottom: 1rem;
    }

    > * {
      width: 100%;
    }

    .content {
      div {
        padding: helpers.px-to-rem(10);
        @include mq.mobileOnly() {
          width: 100% !important;
        }
      }

      hr {
        margin: 1.4rem 0;
      }

      // Responsive YouTube embeds in product descriptions
      iframe {
        width: 100%;
        height: auto;
        aspect-ratio: 16 / 9;
        max-width: 100%;
        display: block;
      }
    }
  }

  &__related {

    .related-products__container {
      background-color: transparent;
    }
  }

  &__content ul,
  &__article ul {
    padding-left: 1.5rem;
  }
}
</style>
